import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API, { setAuthToken, setOrthancAuthCookie, setAccessToken } from '../../services/api.ts';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import styles from './Login.module.css';
import { useNavigate, Link } from 'react-router-dom';

const Register = ({ setIsAuthenticated }) => {
  const [username, setUsername] = useState('');
  const [first_name, setFirstname] = useState('');
  const [last_name, setLastname] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [showPassword, setShowPassword]         = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Optional reader-study fields. Can be filled later via the study-profile modal.
  const [specialty, setSpecialty] = useState('');
  const [trainingLevel, setTrainingLevel] = useState('');
  const [yearsPractice, setYearsPractice] = useState('');
  const [priorLLMUse, setPriorLLMUse] = useState('');

  const navigate = useNavigate();

  const togglePasswordVisibility = () => setShowPassword((s) => !s);
  const toggleConfirmVisibility  = () => setShowConfirmPassword((s) => !s);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);

      // 1) Create account (demographic fields optional — sent only when set)
      const registerPayload = {
        username,
        email,
        password,
        first_name,
        last_name,
      };
      if (specialty) registerPayload.specialty = specialty;
      if (trainingLevel) registerPayload.training_level = trainingLevel;
      if (yearsPractice !== '') {
        const n = parseInt(yearsPractice, 10);
        if (!Number.isNaN(n) && n >= 0) registerPayload.years_independent_practice = n;
      }
      if (priorLLMUse) registerPayload.prior_llm_use = priorLLMUse;
      await API.post('/accounts/register/', registerPayload);

      toast.success('Account created! Logging you in...');

      // 2) Auto-login
      const loginRes = await API.post('/accounts/login/', { username, password });
      const { access, refresh } = loginRes.data;

      setAccessToken(access);
      localStorage.setItem('refresh_token', refresh);

      setAuthToken(access);
      setIsAuthenticated(true);

      // Set orthanc_auth cookie for OHIF viewer auth via nginx
      setOrthancAuthCookie(access);

      setTimeout(() => navigate('/Dashboard'), 1500);
      
    } catch (err) {
      // Try to show server-provided errors if available
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        Object.values(err?.response?.data || {})?.[0]?.[0] ||
        'Registration failed. Please check your details and try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${styles.login} d-flex flex-column align-items-center justify-content-center`}>
      {/* Back to landing */}
      <Link to="/" className="position-absolute top-0 start-0 m-3 btn btn-outline-secondary btn-sm">
        <i className="fa fa-arrow-left me-1"></i> Back
      </Link>

      <ToastContainer autoClose={3000} />

      {/* Logo Section */}
      <div className="d-flex align-items-center justify-content-center gap-4 mb-4">
        <div className={`${styles.bgImage1}`}>
          <img src="/assets/MountSinaiLogo.png" alt="logo" className="img-fluid" />
        </div>
      </div>

      <div className="container">
        <div className="row">
          <div className="col-md-4 col-lg-6 mx-auto">
            <div className="row">
              <div className="col text-start">
                <h3 className={`${styles.loginHeading} mb-2`}>Create your account</h3>
                <p className="text-muted mb-4">
                  Already have one? <Link to="/login">Log in</Link>
                </p>
              </div>
            </div>

            <form onSubmit={handleRegister} className="was-validated" noValidate>

              {/* Firstname & Lastname */}
              <div className="row mb-3">
                <div className="col">
                  <div className="input-group">
                    <span className="input-group-text" id={styles.inputGroupText}>
                      <i className="fa fa-user"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="First name"
                      value={first_name}
                      onChange={(e) => setFirstname(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col">
                  <div className="input-group">
                    <span className="input-group-text" id={styles.inputGroupText}>
                      <i className="fa fa-user"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Last name"
                      value={last_name}
                      onChange={(e) => setLastname(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Username */}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text" id={styles.inputGroupText}>
                    <i className="fa fa-user"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text" id={styles.inputGroupText}>
                    <i className="fa fa-envelope"></i>
                  </span>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="mb-3">
                <div className="input-group">
                  <span className="input-group-text" id={styles.inputGroupText}>
                    <i className="fa fa-key"></i>
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                  <span
                    className="input-group-text"
                    id={styles.eye}
                    style={{ cursor: 'pointer' }}
                    onClick={togglePasswordVisibility}
                    aria-label="Toggle password visibility"
                  >
                    <i className={`fa ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                  </span>
                </div>
                <small className="text-muted">Use at least 8 characters.</small>
              </div>

              {/* Confirm Password */}
              <div className="mb-4">
                <div className="input-group">
                  <span className="input-group-text" id={styles.inputGroupText}>
                    <i className="fa fa-check"></i>
                  </span>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder="Confirm password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                  <span
                    className="input-group-text"
                    id={styles.eye}
                    style={{ cursor: 'pointer' }}
                    onClick={toggleConfirmVisibility}
                    aria-label="Toggle confirm password visibility"
                  >
                    <i className={`fa ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                  </span>
                </div>
              </div>

              {/* Optional reader-study fields */}
              <details className="mb-3">
                <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                  Reader study info (optional) — speeds up first-time entry to a study
                </summary>
                <div className="row mt-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Specialty</label>
                    <select
                      className="form-select"
                      value={specialty}
                      onChange={(e) => setSpecialty(e.target.value)}
                    >
                      <option value="">Select specialty</option>
                      <option value="radiology">Radiology</option>
                      <option value="internal_medicine">Internal Medicine</option>
                      <option value="emergency_medicine">Emergency Medicine</option>
                      <option value="cardiology">Cardiology</option>
                      <option value="ophthalmology">Ophthalmology</option>
                      <option value="general_practice">General Practice</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Training level</label>
                    <select
                      className="form-select"
                      value={trainingLevel}
                      onChange={(e) => setTrainingLevel(e.target.value)}
                    >
                      <option value="">Select level</option>
                      <option value="medical_student">Medical student</option>
                      <option value="resident">Resident</option>
                      <option value="fellow">Fellow</option>
                      <option value="attending">Attending</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Years of independent practice</label>
                    <input
                      type="number"
                      min="0"
                      className="form-control"
                      placeholder="0"
                      value={yearsPractice}
                      onChange={(e) => setYearsPractice(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Prior LLM use</label>
                    <select
                      className="form-select"
                      value={priorLLMUse}
                      onChange={(e) => setPriorLLMUse(e.target.value)}
                    >
                      <option value="">Select frequency</option>
                      <option value="never">Never</option>
                      <option value="rarely">Rarely</option>
                      <option value="monthly">Monthly</option>
                      <option value="weekly">Weekly</option>
                      <option value="daily">Daily</option>
                    </select>
                  </div>
                </div>
              </details>

              {/* Submit */}
              <div className="d-grid gap-2">
                <button type="submit" className="btn btn-danger" disabled={submitting}>
                  <i className="fa fa-user-plus"></i> {submitting ? 'Creating account...' : 'Register'}
                </button>
              </div>

              {/* Optional: policy note */}
              <div className="form-text mt-3">
                By creating an account, you agree to our terms and privacy policy.
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;